import { useEffect, useId, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  APPOINTMENT_MAX_DURATION_MINUTES,
  APPOINTMENT_NOTES_MAX_LENGTH,
  APPOINTMENT_PURPOSE_MAX_LENGTH,
  APPOINTMENT_SLOT_MINUTES,
  APPOINTMENT_SLOT_STARTS,
  createAppointmentSchema,
  updateAppointmentSchema,
  type Appointment,
  type AppointmentConflict,
} from '@tailoring-crm/shared';
import { appointmentConflictOf } from '../../lib/appointments/appointments-api.js';
import { dayKeyOf, slotInstant, slotKeyOf } from '../../lib/appointments/week.js';
import {
  useCreateAppointment,
  useUpdateAppointment,
} from '../../lib/appointments/use-appointments.js';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useCustomerSearch } from '../../lib/customers/use-customers.js';
import { useStaffList } from '../../lib/staff/use-staff.js';
import { formatMinutes, formatMobile, formatTime } from '../../lib/format.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Booking a new appointment, or moving an existing one.
 *
 * ONE DIALOG, TWO MODES, because the two are the same decision made twice: who, with whom, and
 * when. The one thing reschedule may not touch is who — `customerId` never appears in
 * `updateAppointmentSchema` at all, and this form matches that by showing the customer as a
 * fixed line rather than a search box.
 *
 * THE CONFLICT IS NOT A GENERIC FAILURE. The server, not this form, owns whether a staff
 * member's day allows a slot — see `appointmentsOverlap` on the shared schema — so nothing here
 * pre-checks before submitting. When the server says no with `appointment_overlap`, the dialog
 * stays exactly as it was typed and names what it collided with, because "that slot is taken" is
 * a useless sentence next to "Priya Nair, 4 to 5 pm".
 */

interface FormValues {
  staffEmail: string;
  day: string;
  startTime: string;
  durationMinutes: number;
  purpose: string;
  notes: string;
}

const DURATION_OPTIONS: readonly number[] = Array.from(
  { length: APPOINTMENT_MAX_DURATION_MINUTES / APPOINTMENT_SLOT_MINUTES },
  (_unused, index) => (index + 1) * APPOINTMENT_SLOT_MINUTES,
);

export interface AppointmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when moving an existing booking; absent when booking a new one. */
  appointment?: Appointment | null;
  /** Where a new booking opens from — the grid cell that was clicked, or today's first slot. */
  prefill?: { dayKey: string; startTime: string } | null;
  onSaved?: (appointment: Appointment) => void;
}

export function AppointmentFormDialog({
  open,
  onOpenChange,
  appointment = null,
  prefill = null,
  onSaved,
}: AppointmentFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const isReschedule = appointment !== null;
  const create = useCreateAppointment();
  const update = useUpdateAppointment();
  const staffList = useStaffList();

  const initialValues = useMemo<FormValues>(() => {
    if (appointment) {
      const startSlot = slotKeyOf(appointment.startAt);
      return {
        staffEmail: appointment.staffEmail,
        day: dayKeyOf(appointment.startAt),
        startTime: APPOINTMENT_SLOT_STARTS.includes(startSlot)
          ? startSlot
          : (APPOINTMENT_SLOT_STARTS[0] ?? startSlot),
        durationMinutes: appointment.durationMinutes,
        purpose: appointment.purpose,
        notes: appointment.notes ?? '',
      };
    }
    return {
      staffEmail: '',
      day: prefill?.dayKey ?? dayKeyOf(Date.now()),
      startTime: prefill?.startTime ?? APPOINTMENT_SLOT_STARTS[0] ?? '10:00',
      durationMinutes: APPOINTMENT_SLOT_MINUTES,
      purpose: '',
      notes: '',
    };
  }, [appointment, prefill]);

  const [values, setValues] = useState<FormValues>(initialValues);
  const [customerId, setCustomerId] = useState(appointment?.customerId ?? '');
  const [customerName, setCustomerName] = useState(appointment?.customerName ?? '');
  const [customerQuery, setCustomerQuery] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<AppointmentConflict | null>(null);

  // Reopening for a different booking must not carry the previous one's state.
  useEffect(() => {
    if (!open) return;
    setValues(initialValues);
    setCustomerId(appointment?.customerId ?? '');
    setCustomerName(appointment?.customerName ?? '');
    setCustomerQuery('');
    setFieldErrors({});
    setFormError(null);
    setConflict(null);
  }, [open, initialValues, appointment]);

  const customers = useCustomerSearch(
    { q: customerQuery.trim(), pageSize: 5 },
    { enabled: !isReschedule && customerQuery.trim().length > 0 && customerId === '' },
  );

  const set = <K extends keyof FormValues>(field: K, value: FormValues[K]): void => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setFieldErrors((previous) => {
      if (!(field in previous)) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const applyIssues = (issues: readonly { path: PropertyKey[]; message: string }[]): void => {
    const next: Record<string, string> = {};
    for (const issue of issues) {
      const path = issue.path.map(String).join('.');
      next[path] ??= issue.message;
    }
    setFieldErrors(next);
  };

  const handleSubmitError = (error: unknown): void => {
    const found = appointmentConflictOf(error);
    if (found) {
      setConflict(found);
      return;
    }
    if (isApiRequestError(error) && error.fieldErrors) {
      const next: Record<string, string> = {};
      for (const [path, messages] of Object.entries(error.fieldErrors)) {
        next[path] = messages.join(' ');
      }
      setFieldErrors(next);
      setFormError(Object.keys(next).length > 0 ? null : error.message);
      return;
    }
    setFormError(error instanceof Error ? error.message : t('error.unexpected'));
  };

  const submitReschedule = async (): Promise<void> => {
    if (!appointment) return;

    const patch: Record<string, unknown> = {};
    const newStartAt = slotInstant(values.day, values.startTime);
    if (values.staffEmail !== appointment.staffEmail) patch.staffEmail = values.staffEmail;
    if (newStartAt !== appointment.startAt) patch.startAt = newStartAt;
    if (values.durationMinutes !== appointment.durationMinutes) {
      patch.durationMinutes = values.durationMinutes;
    }
    if (values.purpose.trim() !== appointment.purpose) patch.purpose = values.purpose.trim();
    const normalizedNotes = values.notes.trim() || null;
    if (normalizedNotes !== (appointment.notes ?? null)) patch.notes = normalizedNotes;

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    const parsed = updateAppointmentSchema.safeParse(patch);
    if (!parsed.success) {
      applyIssues(parsed.error.issues);
      return;
    }

    try {
      const saved = await update.mutateAsync({ appointmentId: appointment.id, patch: parsed.data });
      toast({ title: t('appointments.toast.rescheduled'), variant: 'success' });
      onSaved?.(saved);
      onOpenChange(false);
    } catch (error) {
      handleSubmitError(error);
    }
  };

  const submitCreate = async (): Promise<void> => {
    if (!customerId) {
      setFormError(t('appointments.form.customerRequired'));
      return;
    }

    const payload = {
      customerId,
      staffEmail: values.staffEmail,
      startAt: slotInstant(values.day, values.startTime),
      durationMinutes: values.durationMinutes,
      purpose: values.purpose.trim(),
      notes: values.notes.trim() || null,
    };
    const parsed = createAppointmentSchema.safeParse(payload);
    if (!parsed.success) {
      applyIssues(parsed.error.issues);
      return;
    }

    try {
      const saved = await create.mutateAsync(parsed.data);
      toast({ title: t('appointments.toast.booked'), variant: 'success' });
      onSaved?.(saved);
      onOpenChange(false);
    } catch (error) {
      handleSubmitError(error);
    }
  };

  const submit = (): void => {
    setFieldErrors({});
    setFormError(null);
    setConflict(null);
    void (isReschedule ? submitReschedule() : submitCreate());
  };

  const pending = create.isPending || update.isPending;

  const errorFor = (field: string): ReactElement | null =>
    fieldErrors[field] ? (
      <p
        id={`${fieldId}-${field}-error`}
        role="alert"
        className="text-sm font-medium text-destructive"
      >
        {fieldErrors[field]}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={`${fieldId}-description`}>
        <DialogHeader>
          <DialogTitle>
            {isReschedule && appointment
              ? t('appointments.form.rescheduleTitle', { name: appointment.customerName })
              : t('appointments.form.bookTitle')}
          </DialogTitle>
          <DialogDescription id={`${fieldId}-description`}>
            {t('appointments.form.hint')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {formError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {formError}
            </p>
          ) : null}

          {conflict ? (
            <div
              role="alert"
              className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 px-3 py-3"
            >
              <p className="text-sm font-semibold">{t('appointments.form.conflict.title')}</p>
              <p className="text-sm text-muted-foreground">
                {t('appointments.form.conflict.description', {
                  name: conflict.conflicts[0]?.customerName ?? '',
                  start: conflict.conflicts[0] ? formatTime(conflict.conflicts[0].startAt) : '',
                  end: conflict.conflicts[0] ? formatTime(conflict.conflicts[0].endAt) : '',
                })}
              </p>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label>{t('appointments.form.chooseCustomer')}</Label>
            {isReschedule ? (
              <div className="rounded-lg border border-border p-3">
                <span className="font-medium">{customerName}</span>
              </div>
            ) : customerId ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3">
                <span className="font-medium">{customerName}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCustomerId('');
                    setCustomerName('');
                  }}
                >
                  {t('appointments.form.changeCustomer')}
                </Button>
              </div>
            ) : (
              <>
                <Label htmlFor={`${fieldId}-customer-search`} className="sr-only">
                  {t('appointments.form.searchCustomer')}
                </Label>
                <Input
                  id={`${fieldId}-customer-search`}
                  type="search"
                  value={customerQuery}
                  onChange={(event) => setCustomerQuery(event.target.value)}
                  placeholder={t('appointments.form.searchCustomer')}
                />
                {customerQuery.trim() ? (
                  <ul className="flex flex-col gap-2">
                    {(customers.data?.items ?? []).map((customer) => (
                      <li key={customer.id}>
                        <button
                          type="button"
                          className="flex w-full flex-col rounded-lg border border-border p-3 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => {
                            setCustomerId(customer.id);
                            setCustomerName(customer.name);
                            setCustomerQuery('');
                          }}
                        >
                          <span className="font-medium">{customer.name}</span>
                          <span className="text-sm text-muted-foreground">
                            {formatMobile(customer.primaryPhone)}
                          </span>
                        </button>
                      </li>
                    ))}
                    {!customers.isFetching && (customers.data?.items ?? []).length === 0 ? (
                      <li className="text-sm text-muted-foreground">
                        {t('appointments.form.noCustomerResults')}
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-staff`}>{t('appointments.form.staffLabel')}</Label>
            <select
              id={`${fieldId}-staff`}
              value={values.staffEmail}
              onChange={(event) => set('staffEmail', event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              aria-invalid={fieldErrors.staffEmail ? true : undefined}
              aria-describedby={fieldErrors.staffEmail ? `${fieldId}-staffEmail-error` : undefined}
            >
              <option value="">{t('appointments.form.chooseStaff')}</option>
              {(staffList.data?.items ?? []).map((member) => (
                <option key={member.userId} value={member.email}>
                  {member.name}
                </option>
              ))}
            </select>
            {errorFor('staffEmail')}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-day`}>{t('appointments.form.dayLabel')}</Label>
              <Input
                id={`${fieldId}-day`}
                type="date"
                value={values.day}
                onChange={(event) => set('day', event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-startTime`}>
                {t('appointments.form.startTimeLabel')}
              </Label>
              <select
                id={`${fieldId}-startTime`}
                value={values.startTime}
                onChange={(event) => set('startTime', event.target.value)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                {APPOINTMENT_SLOT_STARTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {errorFor('startAt')}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-duration`}>{t('appointments.form.durationLabel')}</Label>
            <select
              id={`${fieldId}-duration`}
              value={values.durationMinutes}
              onChange={(event) => set('durationMinutes', Number(event.target.value))}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {formatMinutes(minutes)}
                </option>
              ))}
            </select>
            {errorFor('durationMinutes')}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-purpose`}>{t('appointments.form.purposeLabel')}</Label>
            <Input
              id={`${fieldId}-purpose`}
              value={values.purpose}
              onChange={(event) => set('purpose', event.target.value)}
              placeholder={t('appointments.form.purposePlaceholder')}
              maxLength={APPOINTMENT_PURPOSE_MAX_LENGTH}
              aria-invalid={fieldErrors.purpose ? true : undefined}
              aria-describedby={fieldErrors.purpose ? `${fieldId}-purpose-error` : undefined}
            />
            {errorFor('purpose')}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-notes`}>{t('appointments.form.notesLabel')}</Label>
            <Textarea
              id={`${fieldId}-notes`}
              value={values.notes}
              onChange={(event) => set('notes', event.target.value)}
              placeholder={t('appointments.form.notesPlaceholder')}
              maxLength={APPOINTMENT_NOTES_MAX_LENGTH}
            />
            {errorFor('notes')}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button disabled={pending} onClick={submit}>
            {isReschedule
              ? t('appointments.form.submitReschedule')
              : t('appointments.form.submitBook')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AppointmentFormDialog;
